// Pure derivations for the AI Command Center: tool status lines, the Today /
// Needs-attention cards, the recent-activity feed, and the structured
// dashboard context handed to Counter Intelligence. All inputs are plain data
// (Supabase rows / edge-function payloads) so everything here is unit-testable.

const DAY_MS = 24 * 60 * 60 * 1000;

export const isSessionCompleted = (s) => {
  const status = String(s?.status || '').toLowerCase();
  return status === 'completed' || Boolean(s?.completed_at);
};

export const isSessionInProgress = (s) => {
  if (isSessionCompleted(s)) return false;
  const status = String(s?.status || '').toLowerCase();
  return status === 'in_progress' || Boolean(s?.started_at);
};

/** Future (or today) sessions that aren't finished, soonest first. */
export function upcomingSessions(sessions, todayIso) {
  return (sessions || [])
    .filter((s) => s?.session_date && s.session_date >= todayIso && !isSessionCompleted(s))
    .sort((a, b) => String(a.session_date).localeCompare(String(b.session_date)));
}

/** Vendors with no way to reach them (no email, phone, or contact name). */
export function vendorsMissingContact(vendors) {
  return (vendors || [])
    .filter((v) => {
      const has = (x) => Boolean(x && String(x).trim());
      return !has(v.contact_email) && !has(v.contact_phone) && !has(v.contact_name);
    })
    .map((v) => ({ id: v.id, name: v.display_name || v.name || 'Unnamed vendor' }));
}

/**
 * Setup summary from the setup-progress edge payload. Returns null when setup
 * is complete (or the payload is missing) so finished stores show no setup
 * card at all.
 */
export function summarizeSetup(progressPayload) {
  const summary = progressPayload?.summary;
  if (!summary || !summary.total_count) return null;
  const remaining = Number(summary.remaining_count || 0);
  if (remaining <= 0) return null;
  const next = (progressPayload.steps || [])
    .slice(0, 2)
    .map((s) => ({ key: s.key, title: s.title, href: s.href || null, cta: s.cta || null }))
    .filter((s) => s.title);
  const recommended = progressPayload.recommended_next_step || null;
  return {
    completed: Number(summary.completed_count || 0),
    total: Number(summary.total_count || 0),
    next,
    continueHref: (recommended && recommended.href) || (next[0] && next[0].href) || '/add-items.html',
  };
}

const plural = (n, word, pluralWord) => `${n} ${n === 1 ? word : (pluralWord || `${word}s`)}`;

/**
 * One-line status per tool key, shown under the tool label. Only ever short,
 * factual, and optional — a tool with nothing useful to say gets a static
 * descriptor rather than noise.
 */
export function deriveToolStatuses({
  sessions = [],
  todayIso = '',
  itemCount = null,
  actionablePOs = null,
  vendorsMissingContactCount = 0,
} = {}) {
  const inProgress = (sessions || []).filter(isSessionInProgress).length;
  const upcoming = upcomingSessions(sessions, todayIso).length;

  let counts = 'Start a count';
  if (inProgress > 0) counts = `${plural(inProgress, 'count')} in progress`;
  else if (upcoming > 0) counts = `${upcoming} upcoming`;

  let purchasing = 'Orders & invoices';
  if (actionablePOs > 0) purchasing = `${plural(actionablePOs, 'order')} need${actionablePOs === 1 ? 's' : ''} attention`;
  else if (vendorsMissingContactCount > 0) purchasing = `${plural(vendorsMissingContactCount, 'vendor')} need${vendorsMissingContactCount === 1 ? 's' : ''} contacts`;

  return {
    counts,
    items: itemCount != null ? `${plural(itemCount, 'item')} in catalog` : 'Your catalog',
    purchasing,
    reports: 'View performance',
    recipes: 'Cost a menu item',
    admin: 'Store & account',
    support: 'Help & guides',
    classic: 'Original dashboard',
  };
}

const shortDate = (isoDate) => {
  if (!isoDate) return '';
  const dt = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * The prioritized Needs-attention cards for the Today panel. Sections with
 * nothing actionable are omitted entirely (no clutter); the two "quiet state"
 * cards the product spec keeps (no upcoming counts / no invoice activity) are
 * informational rather than urgent.
 *
 * Card: { key, title, lines[], badge?, tone: 'warn'|'info', actions: [{label, href}] }
 */
export function deriveNeedsAttention({
  setup = null,
  vendorsMissing = [],
  sessions = [],
  todayIso = '',
  actionablePOs = 0,
  priceChanges7d = 0,
  invoices30d = 0,
} = {}) {
  const cards = [];

  if (setup) {
    cards.push({
      key: 'setup',
      title: 'Setup progress',
      lines: [
        `${setup.completed} of ${setup.total} complete`,
        ...setup.next.map((s) => `• ${s.title}`),
      ],
      badge: String(setup.total - setup.completed),
      tone: 'warn',
      actions: [{ label: 'Continue setup', href: setup.continueHref }],
    });
  }

  if (vendorsMissing.length > 0) {
    const names = vendorsMissing.slice(0, 3).map((v) => v.name).join(', ');
    cards.push({
      key: 'vendors',
      title: 'Vendor orders',
      lines: [
        `${plural(vendorsMissing.length, 'vendor is', 'vendors are')} missing contact information.`,
        names ? `• ${names}${vendorsMissing.length > 3 ? '…' : ''}` : null,
      ].filter(Boolean),
      badge: String(vendorsMissing.length),
      tone: 'warn',
      actions: [
        { label: 'Fix vendor contacts', href: '/vendor-editor.html' },
        { label: 'Open ordering', href: '/ordering.html' },
      ],
    });
  }

  if (actionablePOs > 0) {
    cards.push({
      key: 'orders',
      title: 'Orders need attention',
      lines: [`${plural(actionablePOs, 'purchase order')} — drafts, blocked, or undelivered.`],
      badge: String(actionablePOs),
      tone: 'warn',
      actions: [{ label: 'Open ordering', href: '/ordering.html' }],
    });
  }

  const upcoming = upcomingSessions(sessions, todayIso);
  const horizon = todayIso
    ? new Date(new Date(`${todayIso}T00:00:00`).getTime() + 14 * DAY_MS).toISOString().slice(0, 10)
    : '';
  const within14 = upcoming.filter((s) => !horizon || s.session_date <= horizon);
  if (within14.length > 0) {
    const nextUp = within14[0];
    cards.push({
      key: 'counts',
      title: 'Upcoming count',
      lines: [`${nextUp.name || 'Scheduled count'} — ${shortDate(nextUp.session_date)}`],
      tone: 'info',
      actions: [{ label: 'Open counts', href: '/count.html?view=overview' }],
    });
  } else {
    cards.push({
      key: 'counts',
      title: 'Upcoming counts',
      lines: ['No counts scheduled in the next 14 days.'],
      tone: 'info',
      actions: [{ label: 'Create count', href: '/count.html?view=overview' }],
    });
  }

  const activityLines = [];
  if (priceChanges7d > 0) activityLines.push(`${plural(priceChanges7d, 'item price')} changed this week.`);
  if (invoices30d > 0) activityLines.push(`${plural(invoices30d, 'invoice')} received in the last 30 days.`);
  if (activityLines.length === 0) activityLines.push('No invoices received in the last 30 days.');
  cards.push({
    key: 'prices',
    title: 'Price & invoice activity',
    lines: activityLines,
    tone: 'info',
    actions: [
      { label: 'Open invoices', href: '/invoice-inbox.html' },
      ...(priceChanges7d > 0 ? [{ label: 'Review price changes', href: '/items.html' }] : []),
    ],
  });

  return cards;
}

/** Count shown in badges and the collapsed drawer ("Today · N need attention"). */
export function needsAttentionCount(cards) {
  return (cards || []).filter((c) => c.tone === 'warn').length;
}

/**
 * Recent activity feed: completed counts, sent purchase orders, received
 * invoices — merged, newest first. Item: { ts, kind, title, meta, href }.
 */
export function deriveRecentActivity({ sessions = [], purchaseOrders = [], invoices = [], limit = 8 } = {}) {
  const items = [];
  (sessions || []).filter(isSessionCompleted).forEach((s) => {
    const ts = Date.parse(s.completed_at || s.updated_at || '') || 0;
    if (!ts) return;
    items.push({ ts, kind: 'count', title: `${s.name || 'Count'} completed`, href: '/count.html?view=overview' });
  });
  (purchaseOrders || []).forEach((po) => {
    const ts = Date.parse(po.sent_at || '') || 0;
    if (!ts) return;
    const vendor = po.vendor_name_snapshot || 'vendor';
    items.push({ ts, kind: 'order', title: `Order sent to ${vendor}`, href: '/ordering.html' });
  });
  (invoices || []).forEach((inv) => {
    const ts = Date.parse(inv.received_at || inv.processed_at || '') || 0;
    if (!ts) return;
    const vendor = inv.vendor_name || inv.from_email || 'vendor';
    items.push({ ts, kind: 'invoice', title: `Invoice from ${vendor}`, href: '/invoice-inbox.html' });
  });
  return items.sort((a, b) => b.ts - a.ts).slice(0, limit);
}

/**
 * The structured current-state context published for Counter Intelligence
 * (window.__accDashboardStatus → the drawer's support calls → the edge
 * function's DASHBOARD_CONTEXT block). Facts only — no prose, no advice —
 * so the model formats and reasons over live figures instead of guesses.
 */
export function buildDashboardAIContext({
  setup = null,
  vendorsMissing = [],
  sessions = [],
  todayIso = '',
  actionablePOs = null,
  itemCount = null,
  priceChanges7d = null,
  invoices30d = null,
  messagesCount = null,
} = {}) {
  const upcoming = upcomingSessions(sessions, todayIso).slice(0, 3)
    .map((s) => ({ name: s.name || 'Count', date: s.session_date }));
  return {
    surface: 'dashboard-develop',
    setup: setup
      ? { completed: setup.completed, total: setup.total, next_steps: setup.next.map((s) => s.title) }
      : { complete: true },
    vendors_missing_contact: {
      count: vendorsMissing.length,
      names: vendorsMissing.slice(0, 3).map((v) => v.name),
    },
    counts: {
      in_progress: (sessions || []).filter(isSessionInProgress).length,
      upcoming,
    },
    orders_needing_attention: actionablePOs,
    catalog_items: itemCount,
    price_changes_last_7_days: priceChanges7d,
    invoices_last_30_days: invoices30d,
    unread_messages: messagesCount,
  };
}
